/**
 * Search module - AI move selection using Minimax algorithm
 * 
 * This module implements the Minimax algorithm for finding the best move
 * in a chess position. Minimax is a recursive depth-first search that
 * alternates between maximizing (white) and minimizing (black) players.
 * 
 * The search integrates with the opening book to play known opening theory
 * instead of calculating from scratch, improving both quality and speed.
 */

import { GameState, Move } from './types';
import { evaluate, PIECE_VALUES } from './evaluation';
import { generateLegalMoves, makeMove } from './moves';
import { isCheckmate, isStalemate, isDraw } from './endgame';
import { getBookMove } from './openingBook';

/**
 * Score representing checkmate (effectively infinite)
 * Positive for white wins, negative for black wins
 */
const CHECKMATE_SCORE = 1000000;

/**
 * Calculates a heuristic score for move ordering
 * Higher scores indicate more promising moves that should be searched first
 * 
 * Move ordering improves alpha-beta pruning efficiency by ~2-3x
 * Currently uses MVV-LVA (Most Valuable Victim - Least Valuable Attacker) for captures
 * 
 * @param move - The move to score
 * @returns Heuristic score for move ordering (higher = better)
 */
function scoreMoveForOrdering(move: Move): number {
  let score = 0;
  
  // MVV-LVA: Prioritize captures of valuable pieces with less valuable attackers
  // Queen takes pawn (900 - 100 = 800) is better than pawn takes queen (100 - 900 = -800)
  // This is very fast (just lookup piece values, no state changes)
  if (move.captured !== undefined) {
    const victimValue = PIECE_VALUES[move.captured.type];
    const attackerValue = PIECE_VALUES[move.piece.type];
    score += 10000 + victimValue - attackerValue; // Base bonus 10000 for captures
  }
  
  // Note: We skip check detection in move ordering as it's too expensive
  // (requires making the move and checking opponent king safety)
  // Future improvements could add: killer moves, history heuristic, hash moves
  
  return score;
}

/**
 * Orders moves by heuristic score for better alpha-beta pruning
 * Modifies the moves array in-place by sorting in descending order
 * 
 * @param moves - Array of legal moves to order
 */
function orderMoves(moves: Move[]): void {
  // Score each move
  const moveScores = moves.map(move => ({
    move,
    score: scoreMoveForOrdering(move)
  }));
  
  // Sort by score descending (best moves first)
  moveScores.sort((a, b) => b.score - a.score);
  
  // Update moves array in-place
  for (let i = 0; i < moves.length; i++) {
    moves[i] = moveScores[i].move;
  }
}


/**
 * Finds the best move for the active player using Minimax with Alpha-Beta Pruning
 * 
 * First checks the opening book for known opening theory. If the position is in
 * the book, returns a book move immediately (no search required). Otherwise, uses
 * minimax search with alpha-beta pruning to find the best move.
 * 
 * @param state - Current game state
 * @param depth - Search depth (number of half-moves to look ahead)
 * @param useBook - Whether to use opening book (default: true)
 * @returns Best move for active player, or null if no legal moves
 * 
 * @example
 * // Search 3 moves ahead for white (or use book if available)
 * const bestMove = findBestMove(gameState, 3);
 * if (bestMove) {
 *   const newState = makeMove(gameState, bestMove);
 * }
 */
export function findBestMove(state: GameState, depth: number, useBook: boolean = true): Move | null {
  // Check opening book first - instant move selection if in book
  if (useBook) {
    const bookMove = getBookMove(state);
    if (bookMove) {
      return bookMove;
    }
  }
  
  // Not in book (or book disabled) - use minimax search
  const legalMoves = generateLegalMoves(state);
  
  // No legal moves - checkmate or stalemate
  if (legalMoves.length === 0) {
    return null;
  }

  // Order moves for better alpha-beta pruning efficiency
  orderMoves(legalMoves);

  // Determine if we're maximizing (white) or minimizing (black)
  const isMaximizingPlayer = state.activeColor === 0; // 0 = White
  
  let bestMove: Move | null = null;
  let bestScore = isMaximizingPlayer ? -Infinity : Infinity;
  let alpha = -Infinity;
  let beta = Infinity;

  // Try each legal move and evaluate resulting position
  for (const move of legalMoves) {
    const newState = makeMove(state, move);
    const score = minimax(newState, depth - 1, alpha, beta, !isMaximizingPlayer);

    // Update best move if this move is better
    if (isMaximizingPlayer) {
      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
      alpha = Math.max(alpha, score);
    } else {
      if (score < bestScore) {
        bestScore = score;
        bestMove = move;
      }
      beta = Math.min(beta, score);
    }
  }

  return bestMove;
}

/**
 * Minimax algorithm with Alpha-Beta Pruning - recursive depth-first search
 * 
 * Evaluates a position by exploring promising move sequences up to the
 * specified depth. Uses alpha-beta pruning to skip branches that cannot
 * affect the final decision. This optimization typically provides a ~10x
 * speedup over plain minimax.
 * 
 * @param state - Current game state to evaluate
 * @param depth - Remaining search depth (0 = leaf node)
 * @param alpha - Best score maximizing player can guarantee (lower bound)
 * @param beta - Best score minimizing player can guarantee (upper bound)
 * @param isMaximizingPlayer - True if white (maximizing), false if black (minimizing)
 * @returns Evaluation score from white's perspective
 * 
 * @remarks
 * - Positive scores favor white, negative scores favor black
 * - Checkmate scores: +CHECKMATE_SCORE (white wins), -CHECKMATE_SCORE (black wins)
 * - Draw scores: 0 (neutral)
 * - Leaf node scores: evaluation function result
 * - Pruning occurs when beta <= alpha (branch cannot affect result)
 */
function minimax(
  state: GameState,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizingPlayer: boolean
): number {
  // Base case 1: Check for game over conditions
  if (isCheckmate(state)) {
    // Checkmate - return score based on who got checkmated
    // If it's white's turn and checkmate, black won (negative score)
    // If it's black's turn and checkmate, white won (positive score)
    return state.activeColor === 0 ? -CHECKMATE_SCORE : CHECKMATE_SCORE;
  }

  if (isStalemate(state) || isDraw(state)) {
    // Draw conditions - neutral score
    return 0;
  }

  // Base case 2: Reached maximum depth - evaluate position
  if (depth === 0) {
    return evaluate(state);
  }

  // Recursive case: Try all legal moves
  const legalMoves = generateLegalMoves(state);
  
  // Order moves for better alpha-beta pruning efficiency
  orderMoves(legalMoves);

  if (isMaximizingPlayer) {
    // White's turn - maximize score
    let maxScore = -Infinity;
    
    for (const move of legalMoves) {
      const newState = makeMove(state, move);
      const score = minimax(newState, depth - 1, alpha, beta, false);
      maxScore = Math.max(maxScore, score);
      
      // Alpha-beta pruning: if we found a move that's better than
      // what the minimizing player will allow, no need to search further
      alpha = Math.max(alpha, score);
      if (beta <= alpha) {
        break; // Beta cutoff - prune remaining moves
      }
    }
    
    return maxScore;
  } else {
    // Black's turn - minimize score
    let minScore = Infinity;
    
    for (const move of legalMoves) {
      const newState = makeMove(state, move);
      const score = minimax(newState, depth - 1, alpha, beta, true);
      minScore = Math.min(minScore, score);
      
      // Alpha-beta pruning: if we found a move that's worse than
      // what the maximizing player will allow, no need to search further
      beta = Math.min(beta, score);
      if (beta <= alpha) {
        break; // Alpha cutoff - prune remaining moves
      }
    }
    
    return minScore;
  }
}
